import { Feature } from "@components/actions/map/mapMessages";

const isProd = process.env.REACT_APP_ENVIRONMENT === "PROD";

/**
 * Base URL for the raster-painting helper backend
 */
export const HELPER_BACKEND_API_URL = isProd
  ? "https://data.apps.fao.org/api/v1/raster-painting"
  : "https://data.review.fao.org/api/v1/raster-painting";

export interface MsaRasterResponse {
  bounds: {
    east: number;
    north: number;
    south: number;
    west: number;
  };
  boundsCrs: string;
  patchLayerId: string;
  baseRasterUri: string;
  projectAreaGeoJSON: Feature | null;
  owner?: { userId: string }
}

/**
 * Fetch external project data from the helper backend.
 * Returns raster/geometry info including projectAreaGeoJSON.
 */
export async function fetchProjectExternalData(
  rawProjectId: string
): Promise<MsaRasterResponse> {
  const endpoint = `${HELPER_BACKEND_API_URL}/project/external/${rawProjectId}`;
  const response = await fetch(endpoint, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    cache: "no-store",
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data: MsaRasterResponse = await response.json();

  if (data.projectAreaGeoJSON && typeof data.projectAreaGeoJSON === "string") {
    data.projectAreaGeoJSON = JSON.parse(data.projectAreaGeoJSON as unknown as string);
  }

  return data;
}

// review: https://data.review.fao.org/api/v1/raster-painting
// prod: https://data.apps.fao.org/api/v1/raster-painting

export type MapSaveStatusResponse =
  | "SAVE_SUCCESS"
  | "SAVE_ERROR"
  | "SAVE_IN_PROGRESS"
  | "UNSAVED"
  | { error: string };

/**
 * Fetch the map save status for a project from the helper backend.
 * The endpoint returns the status of the async save operation forwarded to the external service.
 *
 * @param projectId - The raw project ID
 * @returns Status string or error object
 */
export async function fetchMapSaveStatus(
  projectId: string
): Promise<MapSaveStatusResponse> {
  const endpoint = `${HELPER_BACKEND_API_URL}/project/external/${projectId}/status`;

  const response = await fetch(endpoint, {
    method: "GET",
  });

  const text = await response.text();

  // Try to parse as JSON (for error responses like { "error": "..." } or JSON-string status like "SAVE_SUCCESS")
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed.error === "string") {
      return { error: parsed.error };
    }
    if (typeof parsed === "string") {
      return parsed.trim() as MapSaveStatusResponse;
    }
  } catch {
    // Not JSON, treat as plain string status
  }

  return text.trim() as MapSaveStatusResponse;
}
